import { Controller } from "@hotwired/stimulus";
import { useDebounce, useResize } from "stimulus-use";

const DESKTOP_IFRAME_WIDTH = 800;

export default class extends Controller {
  static targets = ["iframe", "submit", "screen"];
  static values = { viewport: { type: String, default: "mobile" } };
  static debounces = [{ name: "refreshPreview", wait: 1000 }];

  connect() {
    useDebounce(this);
    useResize(this, { element: this.screenTarget });
    this.#submit();
  }

  showMobile(event) {
    event.preventDefault();
    this.viewportValue = "mobile";
  }

  showDesktop(event) {
    event.preventDefault();
    this.viewportValue = "desktop";
  }

  refreshPreview() {
    this.#submit();
  }

  viewportValueChanged() {
    // Mirror the viewport onto a short attribute on the controller's element so
    // Tailwind `group-data-[viewport=...]:` variants on descendants can react.
    this.element.dataset.viewport = this.viewportValue;
    this.#applyDesktopScale();
  }

  resize() {
    this.#applyDesktopScale();
  }

  #submit() {
    const form = this.submitTarget.form;
    if (!form) return;

    // On the edit form, Rails form_with(method: :patch) adds a hidden
    // `_method=patch` input. When we retarget at the POST-only preview
    // endpoint, Rack::MethodOverride still reads `_method` from the body
    // and routes as PATCH. Disable the override input around the submit so
    // it's excluded from serialization.
    const methodOverride = form.querySelector('input[name="_method"]');
    if (methodOverride) methodOverride.disabled = true;
    try {
      form.requestSubmit(this.submitTarget);
    } finally {
      if (methodOverride) methodOverride.disabled = false;
    }
  }

  #applyDesktopScale() {
    if (!this.hasIframeTarget || !this.hasScreenTarget) return;

    if (this.viewportValue !== "desktop") {
      this.iframeTarget.style.removeProperty("--preview-scale");
      return;
    }

    const width = this.screenTarget.clientWidth;
    if (!width) return;

    this.iframeTarget.style.setProperty("--preview-scale", width / DESKTOP_IFRAME_WIDTH);
  }
}
